import axios from 'axios'
import { authService } from './authService'

const apiClient = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add JWT
apiClient.interceptors.request.use(
  (config) => {
    const auth = authService.getAuthorizationHeader()
    if (auth) {
      config.headers.Authorization = auth
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// A single in-flight refresh shared by every request that 401s at the same time.
// The teams dashboard fires several authenticated calls in parallel (/api/teams,
// /api/products, /api/features/counts, /api/auth/me). Without de-duping, each one
// runs its own /api/auth/refresh on a 401 — the first to fail clears the token, so
// the rest send "Bearer null", which the backend rejects with
// "Invalid compact JWT string ... Found: 0" and leaves the page stuck loading.
let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const refreshAuth = authService.getRefreshAuthorizationHeader()
  // No usable token — don't send "Bearer null"; signal the caller to log out.
  if (!refreshAuth) return null

  try {
    const refreshResponse = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        Authorization: refreshAuth
      }
    })

    if (!refreshResponse.ok) return null

    const { token, refreshToken: newRefreshToken } = await refreshResponse.json()
    if (!token) return null

    authService.setToken(token)
    if (newRefreshToken) {
      authService.setRefreshToken(newRefreshToken)
    }
    return token
  } catch (refreshError) {
    console.error('Token refresh failed', refreshError)
    return null
  }
}

/**
 * Refreshes the access token, joining a refresh already in flight. Resolves to the new token, or
 * null when the session is really gone. For fetch-based streams, so a stream reconnecting on a 401
 * shares the refresh the Axios calls it raced are already running instead of starting its own.
 */
export function refreshAccessTokenShared(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// Response interceptor for 401 handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      // Reuse an in-flight refresh if one is already running for a sibling request.
      const newToken = await refreshAccessTokenShared()
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return apiClient(originalRequest)
      }

      // Refresh failed — logout
      authService.removeToken()
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export default apiClient
